import CollapsedCard from './CollpasedCard';
import RowCard from './RowCard';
import CardBack from './CardBack';
import CardOverlay from './CardOverlay';
import { useState, useRef } from 'react';
import TechnologySquare from './TechnologySquare';
import { SpecialTechTile, ColonyPill, WarBadge } from './SideTiles';
import BlueBank from './BlueBank';
import YellowBank from './YellowBank';
import { useImages } from '../context/ImageContext';
import CorruptionIcon from '../assets/images/corruption.gif';
import BreakdownTip from './BreakdownTip';
import UnhappyIcon from '../assets/images/unhappy.png';
import { IsMobile, useHover } from '../utils/Mobile';
import ActionBar, { BarTarget, CardAbility } from './ActionBar';
const { GameColors } = require('./Constants/Constants')
const { Overlay } = require('react-bootstrap');
const { AgeOrder, MilitaryCardTypes } = require('@tta/engine/Constants/Constants')
const { ShownName } = require('@tta/engine/Utilities/ShownNames');

// The military hand in a fixed order: events first (territories are events: together, by age), then bonuses, tactics,
// aggressions, wars and pacts; each kind by age. Lists of the hand elsewhere (e.g. picking cards to discard) use it too.
const MilitaryHandOrder = [MilitaryCardTypes.Event, MilitaryCardTypes.Bonus, MilitaryCardTypes.Tactic,
    MilitaryCardTypes.Aggression, MilitaryCardTypes.War, MilitaryCardTypes.Pact];
const HandRank = (card) => MilitaryHandOrder.indexOf(card.type === MilitaryCardTypes.Territory ? MilitaryCardTypes.Event : card.type);
export const SortedMilitaryHand = (hand) => [...hand].sort((x, y) =>
    HandRank(x) - HandRank(y) || AgeOrder.indexOf(x.age) - AgeOrder.indexOf(y.age));

// Icon for each kind of special technology

// One status line: icon, current value, optionally "| +production", then anything else (e.g. resource pools)
function Indicator({icon, label, value, production, breakdown, children})
{
    return (
        <BreakdownTip className="tta-indicator" label={label} breakdown={breakdown}>
            <img src={icon} alt={label}/>
            <span>{value}</span>
            {production !== undefined && <span>| <span className={`tta-progress${production < 0 ? ' negative' : ''}`}>{production < 0 ? '−' : '+'}{Math.abs(production)}</span></span>}
            {children}
        </BreakdownTip>
    );
}

// Extra resources or science for military spending only, beside the counter: "+3 [resource]" (the hover says what
// for). They are not a discount: costs stay the same and these pay what they can, always first (the rules leave no choice).
// Civil: for civil spending (Cleopatra's wonders and urban buildings), shown apart from the red military ones
function MilitaryPools({Pools, Icon, Purpose, Civil = false})
{
    const total = Pools.reduce((sum, pool) => sum + pool.amount, 0);
    if (total === 0)
    {
        return null;
    }
    const sources = Pools.map(pool => `${pool.source}: ${pool.amount}`).join('\n');
    return (
        <span className={`tta-pool${Civil ? ' civil' : ''}`} title={`Extra for ${Purpose} this turn\n${sources}`}>
            +{total}<img src={Icon} alt={Purpose}/>
        </span>
    );
}

// The actions usable this turn as tokens. They are spent from the right, so active ones stay on the left.
// special: how many are out of the ordinary (Hammurabi's military action used as a civil action). They are spent last,
// so they are the leftmost, shown in parentheses.
// restricted: actions for some actions only (Gutenberg's, [{ source, purpose, used }]), after the others in parentheses;
// the current player can choose not to use it (UseRestricted, a checkbox beside it)
function ActionTokens({icon, label, total, remaining, breakdown, special = 0, restricted = [], UseRestricted = true, GameEngine, IsCurrentPlayer})
{
    return (
        <BreakdownTip className="tta-action-tokens" label={`${label}: ${remaining} of ${total} left`} breakdown={breakdown}>
            {Array.from({length: total}).map((_, index) => {
                const token = <img key={index} src={icon} alt={label} className={index >= remaining ? 'used' : undefined}/>;
                return index < special
                    ? <span key={index} className={`tta-special-token${index >= remaining ? ' used' : ''}`}>({token})</span>
                    : token;
            })}
            {restricted.map((action, index) =>
                <span key={`restricted ${index}`} className={`tta-restricted-token${action.used ? ' used' : ''}`}
                    title={`${action.source}: 1 civil action for ${action.purpose}, 1 science or resource cheaper; using it scores 2 culture${action.used ? ' (used this turn)' : ''}`}>
                    (<img src={icon} alt={`${label} (${action.source})`}/>)
                    {IsCurrentPlayer && !action.used &&
                        <input type="checkbox" checked={UseRestricted} onChange={(e) => GameEngine.setUseRestricted(e.target.checked)}
                            title={`Use ${action.source}'s civil action (and its discount) when it can be`}/>}
                </span>)}
        </BreakdownTip>
    );
}

// Face-down events a player prepared, as card backs (named on hover for whoever knows them), or a "none" sign
// Events prepared into the current or future events: as cards in a hand when the one looking knows them (their own),
// else face down, by age
function PreparedEvents({Title, Events})
{
    const known = Events.filter(event => event.card);
    const unknown = Events.filter(event => !event.card);
    return <div className="tta-prepared-events">
        <div className="tta-section-title">{Title}</div>
        {Events.length === 0 && <span className="tta-none" title="None">⦸</span>}
        {known.map(event => <CollapsedCard key={event.card.id} card={event.card}/>)}
        {unknown.length > 0 && <div className="tta-card-backs">
            {unknown.map((event, index) => <CardBack key={index} age={event.age} title={event.age}/>)}
        </div>}
    </div>;
}

// Completed wonders as pictures, then the unfinished one with the resource cost of each stage left to build (with the
// new UI, the other way up: the one being built at the top, then the built ones newest first, the oldest at the bottom).
// A wonder taken this turn is put back from its greyed copy in the card row, not from here.
function WondersBox({PlayerViewModel, Bar, GameEngine, NewUI})
{
    const { images } = useImages();
    const inProgress = PlayerViewModel.wonderInProgress;
    const built = PlayerViewModel.wonders.map(wonder => <CardAbility key={wonder.id} Bar={Bar} On={`built:${wonder.name}`} GameEngine={GameEngine}>
        <CompletedWonder Wonder={wonder} image={images[wonder.image]}
            Tokens={wonder.name === 'Louvre Museum' ? PlayerViewModel.louvreTokens : 0}/>
    </CardAbility>);
    const building = inProgress && <BarTarget key="building" Bar={Bar} TargetKey="wonder" GameEngine={GameEngine}>
        <WonderInProgress key={inProgress.card.id} WonderInProgress={inProgress}
            Stages={!Bar?.active ? (Bar?.wonderStages ?? []).map(option => option.stages) : []}
            OnBuild={GameEngine?.BuildWonderStages}/>
    </BarTarget>;
    return (
        // (with the new UI, no box: each wonder outlined, in purple once built, greyish purple while being built)
        <div className={NewUI ? 'tta-pa-wonders' : 'tta-side-box'} style={{ ...(NewUI ? {} : { backgroundColor: '#decde4' }), flex: '2 0 auto', display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
            {NewUI ? [building, ...built.reverse()] : [...built, building]}
        </div>
    );
}

// A completed wonder as its picture, with the full card shown on hover; Tokens: blue tokens kept on it (the Louvre
// Museum's), above it
function CompletedWonder({Wonder, image, Tokens = 0})
{
    const { images } = useImages();
    const [showOverlay, setShowOverlay, hoverProps] = useHover();
    const componentRef = useRef(null);
    return (
        <div>
            <div ref={componentRef} {...hoverProps}>
                {Tokens > 0 &&
                    <div className="tta-wonder-tokens" title={`${Tokens} blue token${Tokens === 1 ? '' : 's'} kept on it (each can be spent for 2 resources, and gives 1 culture)`}>
                        {Array.from({ length: Tokens }, (_, index) => <img key={index} src={images.BlueCube.src} alt="blue token"/>)}
                    </div>}
                {image
                    ? <img src={image.src} alt={Wonder.name} className={`tta-wonder-image${Wonder.crumbled ? ' crumbled' : ''}`} style={{ display: 'block' }}
                        title={Wonder.crumbled ? `Crumbled ${Wonder.name}` : undefined}/>
                    : (Wonder.crumbled ? `Crumbled ${Wonder.name}` : Wonder.name)}
            </div>
            <CardOverlay ShowOverlay={showOverlay} ComponentRef={componentRef} card={Wonder} callback={() => {}} OnClose={() => setShowOverlay(false)}/>
        </div>
    );
}

// The leader in play as a portrait, with the full card shown on hover; an empty slot when there is none
function LeaderSlot({Leader})
{
    const { images } = useImages();
    const [showOverlay, setShowOverlay, hoverProps] = useHover();
    const componentRef = useRef(null);
    if (!Leader)
    {
        return <div className="tta-leader-slot"/>;
    }
    const portrait = images[Leader.image];
    return (
        <div>
            <div className="tta-leader-slot" ref={componentRef} style={{ overflow: 'hidden' }}
                {...hoverProps}>
                {portrait ? <img src={portrait.src} alt={Leader.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : Leader.name}
            </div>
            <CardOverlay ShowOverlay={showOverlay} ComponentRef={componentRef} card={Leader} callback={() => {}} OnClose={() => setShowOverlay(false)}/>
        </div>
    );
}

// The unfinished wonder: name and remaining stage costs, with the full card shown on hover. With static buttons, Stages:
// how many stages can be built now (each a number of them, Masonry and the like allowing more than one): clicked, its
// next stage is built, or, clicked on a stage, it is built up to that one (OnBuild(stages))
function WonderInProgress({WonderInProgress, Stages = [], OnBuild})
{
    const { images } = useImages();
    const [showOverlay, setShowOverlay, hoverProps] = useHover();
    const componentRef = useRef(null);
    // (the stages that would be built, as many as hovered: over a stage, up to it; elsewhere on it, the next)
    const [upTo, setUpTo] = useState(null);
    const built = WonderInProgress.stagesBuilt;
    const reachable = (index) => Stages.includes(index - built + 1);
    const shown = upTo ?? (Stages.includes(1) ? 1 : 0);
    const [hovering, setHovering] = useState(false);
    return (
        <div>
            <div
                className={`tta-wonder-in-progress${Stages.length > 0 ? ' buildable' : ''}`}
                ref={componentRef}
                {...hoverProps}
                onMouseEnter={(event) => { hoverProps.onMouseEnter?.(event); setHovering(true); }}
                onMouseLeave={(event) => { hoverProps.onMouseLeave?.(event); setHovering(false); setUpTo(null); }}
                onClick={Stages.length > 0 ? () => OnBuild(upTo ?? 1) : hoverProps.onClick}>
                <div>{WonderInProgress.card.name}</div>
                {/* Each built stage is covered by its blue token, so the tokens on the wonder can be counted */}
                <div className="tta-wonder-stages">
                    {WonderInProgress.stageCosts.map((cost, index) => index < built
                        ? <img key={index} src={images.BlueCube.src} alt="built"
                            title={`Built${WonderInProgress.builtBy?.[index] ? ` by ${WonderInProgress.builtBy[index]}` : ''}: a blue token covers this stage`}/>
                        : <span key={index} className={`tta-wonder-stage${reachable(index) ? ' reachable' : ''}`}
                            onMouseEnter={reachable(index) ? () => setUpTo(index - built + 1) : undefined}
                            onMouseLeave={reachable(index) ? () => setUpTo(null) : undefined}>
                            {cost}
                            {hovering && index < built + shown &&
                                <img className="tta-wonder-preview" src={images.BlueCube.src} alt="would be built"/>}
                        </span>)}
                </div>
            </div>
            <CardOverlay
                ShowOverlay={showOverlay}
                ComponentRef={componentRef}
                card={WonderInProgress.card}
                callback={() => {}} //don't want to keep the overlay up
                OnClose={() => setShowOverlay(false)}
            />
        </div>
    );
}

// The pacts the civilization is in: each card, with a cube in each side's color (marked A / B for a one-sided pact)
// and what each side gets on hover
// The armies the player's units form for their tactic (full or obsolete)
const armyCount = (playerViewModel) => (playerViewModel.tacticArmies?.full ?? 0) + (playerViewModel.tacticArmies?.obsolete ?? 0);

// Bar, GameEngine: on the board of the player looking (static buttons), each pact is the action bar's target: clicked
// in the political phase, it is cancelled. TradeRoute: with the new UI, the Trade Routes Agreement's choice (paying 1 of
// a cost in the other currency, once a turn; TradeRoute.use: chosen), made by clicking its card, lit up while chosen
function PactsBox({Pacts, CubeColors, PlayerNames, Bar, GameEngine, TradeRoute})
{
    const trade = (pact) => TradeRoute?.source === pact.card.name ? TradeRoute : null;
    if (!Pacts?.length)
    {
        return null;
    }
    return (
        <div className="tta-pacts" title="Pacts">
            {Pacts.map(pact =>
                // (as a tactic in play: the small card, the cubes on it along the bottom, the full card on hover)
                <div key={pact.card.id} className="tta-pact">
                    {trade(pact)
                        ? <TradeRouteCard Pact={pact} TradeRoute={trade(pact)} GameEngine={GameEngine}/>
                        : <BarTarget Bar={Bar} TargetKey={`card:${pact.card.id}`} GameEngine={GameEngine}><RowCard card={pact.card}/></BarTarget>}
                    {/* Whose it is (who offered it: another pact they offer replaces it), in the top corner, on both sides' cards */}
                    <span className="tta-pact-owner" style={{ backgroundColor: CubeColors[pact.offerer] }}
                        title={`${PlayerNames[pact.offerer]}'s pact (offered it): if ${PlayerNames[pact.offerer]} makes another pact, this one ends`}/>
                    <div className="tta-pact-cubes">
                        {[pact.a, pact.b].map((player, index) =>
                            <span key={player} className="tta-pact-cube" style={{ backgroundColor: CubeColors[player] }}
                                title={`${pact.oneSided ? (index === 0 ? 'A: ' : 'B: ') : ''}${PlayerNames[player]}${pact.offerer === player ? ' (offered it)' : ''}: ${pact.terms[player]}`}>
                                {pact.oneSided ? (index === 0 ? 'A' : 'B') : ''}
                            </span>)}
                    </div>
                </div>)}
        </div>
    );
}

// The Trade Routes Agreement as its own switch: clicked, paying 1 of the next cost in the other currency is chosen (lit
// up) or not; once used this turn, it can't be chosen again until the next
function TradeRouteCard({Pact, TradeRoute, GameEngine})
{
    const used = TradeRoute.left === 0;
    const on = TradeRoute.use && !used;
    const what = `Pay 1 ${TradeRoute.payWith === 'food' ? 'food' : 'resource'} instead of 1 ${TradeRoute.instead === 'food' ? 'food' : 'resource'}`;
    return (
        <div className={`tta-trade-route-card${on ? ' on' : ''}${used ? ' used' : ''}`}
            onClick={used ? undefined : () => GameEngine.setUseTradeRoute(!TradeRoute.use)}>
            <RowCard card={Pact.card} title={used ? `${what}: already used this turn` : `${what}, once per turn: ${on ? 'on (click to stop)' : 'off (click to use it)'}`}/>
        </div>
    );
}

// The government's name, its card on hover (where its urban building limit is seen)
function Government({Name, Card})
{
    const [shown, setShown, hoverProps] = useHover();
    const ref = useRef(null);
    return (
        <>
            <div ref={ref} className="tta-government" {...hoverProps}>{ShownName(Name)}</div>
            {Card && <CardOverlay ShowOverlay={shown} ComponentRef={ref} card={Card} callback={() => {}} OnClose={() => setShown(false)}/>}
        </>
    );
}

// The left column: status, leader and government, then the wonders / special technologies / colonies strip
// How the player's last turn stands, once the end of the game is known (see GameEnd.LastTurnStatus)
const LastTurnLabels = { 'last turn': 'Last turn', 'next round': 'Last turn next round', done: 'Last turn played' };

// Bar: the action bar's targets (the wonder being built); NewUI: culture and strength left out (see PlayerArea), and
// only as wide as what is in it
function StatusBox({PlayerViewModel, GameEngine, IsCurrentPlayer, UseRestricted, Pacts, Wars, PlayerColors, CubeColors, PlayerNames, LastTurn, Resigned, Bar, NewUI, TradeRoute})
{
    const { images } = useImages();
    const breakdowns = PlayerViewModel.breakdowns;
    // (what the side strip has in it: wonders built or being built, special technologies, colonies)
    const hasWonders = PlayerViewModel.wonders.length > 0 || !!PlayerViewModel.wonderInProgress;
    const hasSpecial = PlayerViewModel.specialTechnologies.length > 0;
    const hasColonies = PlayerViewModel.colonies.length > 0;

    return (
        <div className="tta-pa-status" style={{ display: 'flex', gap: '6px', width: NewUI ? undefined : '450px', flex: '0 0 auto' }}>
            <div className="tta-box" style={{ flex: 1 }}>
                {(Resigned || LastTurn) &&
                    <div className={`tta-last-turn${Resigned || LastTurn === 'done' ? ' done' : ''}`}>{Resigned ? 'Resigned' : LastTurnLabels[LastTurn]}</div>}
                <div className="tta-status-bar">
                    {/* Wars this player is in (declared or declared on), until resolved */}
                    {(Wars ?? []).map((war, index) => {
                        const rival = war.aggressor === PlayerViewModel.playerNumber ? war.defender : war.aggressor;
                        return <WarBadge key={index} war={war} rival={PlayerNames[rival]} color={PlayerColors[rival]}/>;
                    })}
                    {PlayerViewModel.corruption > 0 &&
                        <img src={CorruptionIcon} alt="Corruption" title={`Corruption: lose ${PlayerViewModel.corruption} resources at the end of the turn`}/>}
                    {PlayerViewModel.famine > 0 &&
                        <span className="tta-famine" title={`Famine: ${PlayerViewModel.famine} food short at the end of the turn, losing ${PlayerViewModel.famine * 4} culture`}>
                            <img src={images.Food.src} alt="Famine"/>
                        </span>}
                    {/* Only an uprising is flagged here; discontent workers show on the yellow bank and worker pool */}
                    {PlayerViewModel.uprising &&
                        <img src={UnhappyIcon} alt="Uprising" style={{ width: '20px', height: '20px' }}
                            title={`Uprising: ${PlayerViewModel.discontentWorkers} discontent workers but only ${PlayerViewModel.workerPool} unused. Production will be skipped.`}/>}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                    {/* Fixed width, with room for a resource pool, so the leader and government never move (with the new UI,
                        only as wide as the numbers, leaving the room to the buildings) */}
                    <div className="tta-pa-indicators" style={{ flex: NewUI ? '0 0 auto' : '0 0 130px' }}>
                        {/* (with the new UI, culture and strength are on the boards under the card row instead) */}
                        {!NewUI && <Indicator icon={images.Culture.src} label="Culture" value={PlayerViewModel.culture} production={PlayerViewModel.cultureProduction} breakdown={breakdowns.culture}/>}
                        <Indicator icon={images.Science.src} label="Science" value={PlayerViewModel.science} production={PlayerViewModel.scienceProduction} breakdown={breakdowns.science}>
                            <MilitaryPools Pools={PlayerViewModel.militarySciencePools} Icon={images.Science.src} Purpose="developing military unit technologies"/>
                        </Indicator>
                        {!NewUI && <Indicator icon={images.Strength.src} label="Military strength" value={PlayerViewModel.militaryStrength} breakdown={breakdowns.military}/>}
                        {/* (with the new UI, only while the civilization has a colonization bonus) */}
                        {(!NewUI || !!PlayerViewModel.colonizationBonus) && <Indicator icon={images.ExplorationResource.src} label="Colonization" value={PlayerViewModel.colonizationBonus || '-'} breakdown={breakdowns.colonization}/>}
                        {/* (with the new UI, the 8 spaces a civilization can fill, in 2 rows of 4, those not filled greyed out) */}
                        {NewUI ?
                        <BreakdownTip className="tta-indicator tta-happy-indicator" label="Happy faces" breakdown={breakdowns.happyFaces}>
                            <span className="tta-happy-spaces">{Array.from({length: Math.max(8, PlayerViewModel.happyFaces)}).map((_, index) =>
                                <img key={index} src={images.Happiness.src} alt={index < PlayerViewModel.happyFaces ? 'Happy face' : 'No happy face'}
                                    className={index < PlayerViewModel.happyFaces ? undefined : 'empty'}/>)}</span>
                        </BreakdownTip> :
                        <BreakdownTip className="tta-indicator" label="Happy faces" breakdown={breakdowns.happyFaces}>
                            {PlayerViewModel.happyFaces === 0 ? '-' :
                                <span className="tta-happy-faces">{Array.from({length: PlayerViewModel.happyFaces}).map((_, index) =>
                                    <img key={index} src={images.Happiness.src} alt="Happy face"/>)}</span>}
                        </BreakdownTip>}
                        <Indicator icon={images.Food.src} label="Food" value={PlayerViewModel.currentFood} production={PlayerViewModel.foodProduction} breakdown={breakdowns.food}/>
                        <Indicator icon={images.Resource.src} label="Resources" value={PlayerViewModel.currentResources} production={PlayerViewModel.resourceProduction} breakdown={breakdowns.resources}>
                            {/* (one above the other, to save width) */}
                            <span className="tta-pools">
                                <MilitaryPools Pools={PlayerViewModel.militaryPools} Icon={images.Resource.src} Purpose="building and upgrading military units"/>
                                <MilitaryPools Civil Pools={PlayerViewModel.wonderUrbanPools} Icon={images.Resource.src} Purpose="building wonders, and building and upgrading urban buildings"/>
                            </span>
                        </Indicator>
                        {/* (with the new UI, in the buildings grid's corner instead) */}
                        {!NewUI && <Indicator icon={images.Home.src} label="Urban building limit (of each kind)" value={PlayerViewModel.urbanBuildingLimit} breakdown={breakdowns.urbanBuildingLimit}/>}
                        {/* (with the new UI, the special technologies under the numbers, each outlined in their blue, instead of in the strip at the right) */}
                        {NewUI && hasSpecial &&
                            <div className="tta-pa-specials">
                                {PlayerViewModel.specialTechnologies.map(tech => <SpecialTechTile key={tech.name} tech={tech}/>)}
                            </div>}
                    </div>
                    <div className={NewUI ? 'tta-pa-leader' : undefined} style={NewUI ? { flex: '0 0 auto', minWidth: '98px' } : { flex: 1 }}>
                        <CardAbility Bar={Bar} On="leader" GameEngine={GameEngine}>
                            <LeaderSlot Leader={PlayerViewModel.leader}/>
                        </CardAbility>
                        {/* Yellow tokens kept above the government card (Hippocrates), one for each new age */}
                        {PlayerViewModel.hippocratesTokens > 0 &&
                            <div className="tta-hippocrates" title={`Hippocrates: ${PlayerViewModel.hippocratesTokens} yellow token(s) above the government card; one goes into the yellow bank at each new age`}>
                                {Array.from({ length: PlayerViewModel.hippocratesTokens }, (_, index) => <img key={index} src={images.YellowCube.src} alt="yellow token"/>)}
                            </div>}
                        <Government Name={PlayerViewModel.government} Card={PlayerViewModel.governmentCard}/>
                        <ActionTokens icon={images.CivilAction.src} label="Civil actions"
                            total={PlayerViewModel.civilActionCount} remaining={PlayerViewModel.civilActionsRemaining} breakdown={breakdowns.civilActions}
                            special={PlayerViewModel.militaryAsCivilActions} restricted={PlayerViewModel.restrictedCivilActions}
                            UseRestricted={UseRestricted} GameEngine={GameEngine} IsCurrentPlayer={IsCurrentPlayer}/>
                        <ActionTokens icon={images.MilitaryAction.src} label="Military actions"
                            total={PlayerViewModel.militaryActionCount} remaining={PlayerViewModel.militaryActionsRemaining} breakdown={breakdowns.militaryActions}/>
                        {/* The player's own tactics, as in the military area: the one they are on (only while it is theirs alone, just
                            played; a common one is in the military area, with their marker on it) with their marker, and those kept
                            beside it (replaced while Sun Tzu leads: no one can copy them until he is gone) small */}
                        {((PlayerViewModel.currentTactic && PlayerViewModel.tacticIsPrivate) || PlayerViewModel.keptTactics.length > 0) &&
                            <div className="tta-own-tactics">
                                {PlayerViewModel.currentTactic && PlayerViewModel.tacticIsPrivate &&
                                    <div className="tta-military-tactic">
                                        <RowCard card={PlayerViewModel.currentTactic}/>
                                        <div className="tta-tactic-markers">
                                            {/* (how many armies the units form for it: in the tooltip) */}
                                            <span className="tta-tactic-marker-wrap" title={`On this tactic: ${armyCount(PlayerViewModel)} arm${armyCount(PlayerViewModel) === 1 ? 'y' : 'ies'}`}>
                                                <span className="tta-tactic-marker" style={{ backgroundColor: CubeColors[PlayerViewModel.playerNumber] }}/>
                                            </span>
                                        </div>
                                    </div>}
                                {PlayerViewModel.keptTactics.length > 0 &&
                                    <div className="tta-own-tactics-kept">
                                        {PlayerViewModel.keptTactics.map(tactic => <CollapsedCard key={tactic.id} card={tactic}/>)}
                                    </div>}
                            </div>}
                        <PactsBox Pacts={Pacts} CubeColors={CubeColors} PlayerNames={PlayerNames} Bar={Bar} GameEngine={GameEngine} TradeRoute={TradeRoute}/>
                    </div>
                </div>
            </div>
            {/* (with the new UI, a box shows only once it has something in it, and the strip only once one does; the special
                technologies are under the numbers instead) */}
            {(!NewUI || hasWonders || hasColonies) &&
            <div className="tta-pa-side" style={{ width: '100px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {(!NewUI || hasWonders) && <WondersBox PlayerViewModel={PlayerViewModel} Bar={Bar} GameEngine={GameEngine} NewUI={NewUI}/>}
                {!NewUI &&
                <div className="tta-side-box" style={{ backgroundColor: '#bdc7e2', flex: '1 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    {PlayerViewModel.specialTechnologies.map(tech => <SpecialTechTile key={tech.name} tech={tech}/>)}
                </div>}
                {/* Colonies, each as what it gives while held (its card on hover) */}
                {(!NewUI || hasColonies) &&
                // (with the new UI, no box: the colonies are green already)
                <div className={NewUI ? 'tta-pa-colonies' : 'tta-side-box'} style={{ ...(NewUI ? {} : { backgroundColor: '#cfda8b' }), flex: '1 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    {PlayerViewModel.colonies.map(colony => <ColonyPill key={colony.id} colony={colony}/>)}
                </div>}
            </div>}
        </div>
    );
}

// A player's tab: their name, and on hover a quick summary of their board (the status column's numbers), so it can be
// checked without opening the tab (NewUI: without culture and strength, as the status column)
export function PlayerTabTitle({PlayerViewModel, NewUI})
{
    const { images } = useImages();
    const [show, setShow] = useState(false);
    const target = useRef(null);
    const p = PlayerViewModel;
    // (a phone has no hover: the summary is on the board itself, a tap away)
    if (IsMobile)
    {
        return <span className="tta-tab-name">{p.name}</span>;
    }
    const Line = ({icon, label, value, production}) =>
        <div className="tta-tab-summary-line">
            <img src={icon} alt={label} title={label}/>
            <span>{value}</span>
            {production !== undefined && <span>| <span className={`tta-progress${production < 0 ? ' negative' : ''}`}>{production < 0 ? '−' : '+'}{Math.abs(production)}</span></span>}
        </div>;
    return (
        <>
            <span ref={target} className="tta-tab-name" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>{p.name}</span>
            {/* (against the window, so it never makes the page taller: see CardOverlay) */}
            <Overlay show={show} target={target.current} placement="bottom-start" flip popperConfig={{ strategy: 'fixed' }}>
                {({ arrowProps, hasDoneInitialMeasure, popper, placement, show: _, ...props }) =>
                    <div {...props} className="tta-tab-summary">
                        {!NewUI && <Line icon={images.Culture.src} label="Culture" value={p.culture} production={p.cultureProduction}/>}
                        <Line icon={images.Science.src} label="Science" value={p.science} production={p.scienceProduction}/>
                        {!NewUI && <Line icon={images.Strength.src} label="Military strength" value={p.militaryStrength}/>}
                        {(!NewUI || !!p.colonizationBonus) && <Line icon={images.ExplorationResource.src} label="Colonization" value={p.colonizationBonus || '-'}/>}
                        <div className="tta-tab-summary-line">
                            {p.happyFaces === 0 ? <><img src={images.Happiness.src} alt="Happy faces"/><span>-</span></> :
                                <span className="tta-happy-faces">{Array.from({length: p.happyFaces}).map((_, index) =>
                                    <img key={index} src={images.Happiness.src} alt="Happy face"/>)}</span>}
                        </div>
                        <Line icon={images.Food.src} label="Food" value={p.currentFood} production={p.foodProduction}/>
                        <Line icon={images.Resource.src} label="Resources" value={p.currentResources} production={p.resourceProduction}/>
                    </div>}
            </Overlay>
        </>
    );
}

// ActionBar: the action bar, on the board of the player looking (null on the others), with TurnButtons (End turn, Undo…)
// and, in the political phase, Political (passing it) in place of its buttons. Military: the military area (everyone's
// strength and the tactics), the same on every board, at the top right. NewUI: with the boards under the card row, which
// show culture (points and each turn) and strength, so the status column leaves them out
function PlayerArea({PlayerViewModel, GameEngine, IsCurrentPlayer, RetractableCardIds, UseRestricted, Pacts, Wars, PlayerColors, CubeColors, PlayerNames, LastTurn, Resigned, BoardSelection, ActionBar: Bar, TurnButtons, Political, Military, NewUI, TradeRoute})
{
    const { images } = useImages();
    // (the worker pool starts building while there is a worker in it and Build can be used, or is on)
    const build = Bar?.triggers.find(trigger => trigger.id === 'build');
    const buildFromPool = !!build?.enabled && PlayerViewModel.workerPool > 0 && (!Bar.active || build.active);
    // (the yellow bank increases population, as Population's button does, while it can be used and nothing else is on)
    const population = Bar?.triggers.find(trigger => trigger.id === 'population');
    const growFromBank = !!population?.enabled && !Bar.active;
    // (a card in hand, with static buttons and no trigger on: what using it costs, and why it can't be used)
    const handOf = (card) => !Bar?.active ? Bar?.hand?.[`card:${card.id}`] : null;
    // (a military card, also while a trigger is on: its target's, e.g. an aggression's military actions while picked)
    const militaryOf = (card) => handOf(card) ?? (Bar?.active ? Bar.targets[`card:${card.id}`] : null);

    var getTechHeaderStyle = (techType) =>
    {
        return {
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '13px',
            textAlign: 'center',
            padding: '2px 6px',
            backgroundColor: GameColors.TechnologyColors[techType.replace(/\s/g, '')],
        }
    }

    return (
        <div key={PlayerViewModel.name} className="tta tta-pa" style={{ display: 'flex', gap: NewUI ? '8px 14px' : '14px', alignItems: 'flex-start', ...(NewUI ? { flexWrap: 'wrap' } : {}) }}>
            {/* (with the new UI, the action bar across the top of the board, its buttons in a row) */}
            {NewUI && Bar && <div className="tta-box" style={{ flex: '0 0 100%' }}><ActionBar Wide Bar={Bar} GameEngine={GameEngine} TurnButtons={TurnButtons} Political={Political}/></div>}
            {/* The left column: the status, then (on the board of the player looking) the action bar under it */}
            <div style={{ flex: NewUI ? '0 0 auto' : '0 0 450px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <StatusBox PlayerViewModel={PlayerViewModel} GameEngine={GameEngine} IsCurrentPlayer={IsCurrentPlayer} UseRestricted={UseRestricted}
                    Pacts={Pacts} Wars={Wars} PlayerColors={PlayerColors} CubeColors={CubeColors} PlayerNames={PlayerNames} LastTurn={LastTurn} Resigned={Resigned} Bar={Bar} NewUI={NewUI} TradeRoute={TradeRoute}/>
                {!NewUI && Bar && <div className="tta-box"><ActionBar Bar={Bar} GameEngine={GameEngine} TurnButtons={TurnButtons} Political={Political}/></div>}
            </div>
            {/* (with the new UI, the hands and events played one above the other, to the left of the buildings) */}
            <div className="tta-pa-main" style={{ flex: 1, minWidth: 0, ...(NewUI ? { display: 'flex', gap: '10px', alignItems: 'flex-start' } : {}) }}>
                {/* The hands and the events played, compact, then (the same on every board) the military area at the right */}
                <div className="tta-pa-hands" style={NewUI ? { flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '10px' } : { display: 'flex', gap: '16px', marginBottom: '8px', alignItems: 'flex-start' }}>
                    {/* (each hand as wide as its longest card's name) */}
                    <div className="tta-left tta-pa-hand" style={{ width: 'max-content', minWidth: '150px' }}>
                        <div className="tta-section-title">Civil cards {PlayerViewModel.civilHand.length}/{PlayerViewModel.civilHandLimit}</div>
                        {PlayerViewModel.civilHand.map((card) => {
                            // (with static buttons a click on it does its action: it is put back from its copy in the card row)
                            const retractable = RetractableCardIds.includes(card.id);
                            return <BarTarget key={card.id} Bar={Bar} TargetKey={`card:${card.id}`} GameEngine={GameEngine}>
                                <CollapsedCard
                                    card={card}
                                    highlighted={retractable}
                                    title={retractable && !Bar ? 'Taken this turn; click to put it back' : undefined}
                                    onClick={retractable && !Bar ? () => GameEngine.retractCardTake(card.id) : undefined}
                                    Cost={handOf(card)?.cost} Usable={handOf(card)?.selectable} Note={handOf(card)?.reason}/>
                            </BarTarget>
                        })}
                    </div>
                    <div className="tta-left tta-pa-hand" style={{ width: 'max-content', minWidth: '150px' }}>
                        <div className="tta-section-title">Military cards: {PlayerViewModel.militaryHand.length}</div>
                        {/* Someone else's military cards are face down: only their backs show */}
                        <div className={PlayerViewModel.militaryHand.some(card => card.type === 'Hidden') ? 'tta-card-backs' : undefined}>
                            {SortedMilitaryHand(PlayerViewModel.militaryHand).map((card) => card.type === 'Hidden'
                                ? <CardBack key={card.id} age={card.age}/>
                                : <BarTarget key={card.id} Bar={Bar} TargetKey={`card:${card.id}`} GameEngine={GameEngine}>
                                    <CollapsedCard card={card} Cost={militaryOf(card)?.cost} Usable={militaryOf(card)?.selectable} Note={militaryOf(card)?.reason}/>
                                </BarTarget>)}
                        </div>
                    </div>
                    <div className="tta-left tta-pa-hand tta-pa-events" style={{ width: 'max-content', minWidth: '120px' }}>
                        <PreparedEvents Title="Current events played" Events={PlayerViewModel.eventsPlayed.current}/>
                    </div>
                    <div className="tta-left tta-pa-hand tta-pa-events" style={{ width: 'max-content', minWidth: '120px' }}>
                        <PreparedEvents Title="Future events played" Events={PlayerViewModel.eventsPlayed.future}/>
                    </div>
                    {Military && <div className="tta-pa-military">{Military}</div>}
                </div>
                {/* The banks and the worker pool above the buildings (with the new UI, under them) */}
                <div className="tta-white-box" style={NewUI ? { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' } : undefined}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', ...(NewUI ? { order: 1, marginTop: '10px' } : {}) }}>
                        <BreakdownTip style={{ flex: 1 }} label="Blue tokens owned" breakdown={PlayerViewModel.breakdowns.blueTokens}>
                            <BlueBank count={PlayerViewModel.blueBank} />
                        </BreakdownTip>
                        {/* (with static buttons, clicked to build: Build switched on, as its button does) */}
                        <div className={`tta-worker-pool${buildFromPool ? ' tta-pool-build' : ''}`} title={`${PlayerViewModel.workerPool} unused workers`}
                            onClick={buildFromPool ? () => GameEngine.StartTrigger('build') : undefined}>
                            <div>Worker pool</div>
                            <div>
                                {/* Discontent workers stand in the pool as unhappy faces */}
                                {Array.from({length: PlayerViewModel.workerPool}).map((_, index) => index < PlayerViewModel.discontentWorkers
                                    ? <img key={index} src={UnhappyIcon} alt="Discontent worker" title="Discontent worker"/>
                                    : <img key={index} src={images.Happiness.src} alt="Unused worker" title="Unused worker"/>)}
                                &nbsp;
                            </div>
                        </div>
                    </div>
                    <BreakdownTip label="Yellow tokens owned" breakdown={PlayerViewModel.breakdowns.yellowTokens} style={NewUI ? { order: 2 } : undefined}>
                        {/* (with static buttons, clicked to increase population) */}
                        <div className={growFromBank ? 'tta-bank-grow' : undefined} onClick={growFromBank ? () => GameEngine.StartTrigger('population') : undefined}>
                            <YellowBank count={PlayerViewModel.yellowBank} HappyFaces={PlayerViewModel.happyFaces} Color={CubeColors[PlayerViewModel.playerNumber]}/>
                        </div>
                    </BreakdownTip>
                    {/* Buildings and units by age, newest age on top */}
                    <div className="tta-pa-grid">
                    <table className="tta-building-grid">
                        <tbody>
                            <tr>
                                {/* (with the new UI, the urban building limit in the corner, above the ages) */}
                                <td className="tta-building-age">
                                    {NewUI && <Indicator icon={images.Home.src} label="Urban building limit (of each kind)" value={PlayerViewModel.urbanBuildingLimit} breakdown={PlayerViewModel.breakdowns.urbanBuildingLimit}/>}
                                </td>
                                {PlayerViewModel.techTypesUsed.map((techType) => {
                                    return <td style={getTechHeaderStyle(techType)} key={techType} >
                                        {techType}
                                    </td>
                                })}
                            </tr>
                            {/* (with the new UI, an age with nothing built in it has no row) */}
                            {PlayerViewModel.technologyGrid.filter(techAge => !NewUI || techAge.Techs.some(Boolean)).map((techAge) => {
                                return <tr key={techAge.age}>
                                    <td className="tta-building-age">{techAge.age}</td>
                                    {techAge.Techs.map((tech,index) => {
                                        return <TechnologySquare key={index} Technology={tech}
                                            Selection={tech && BoardSelection?.cells[tech.name]}
                                            OnClick={(name) => GameEngine.ClickBoardCard(name)}
                                            OnUpgrade={tech && !BoardSelection && Bar?.upgradable?.includes(tech.name) ? GameEngine.StartUpgradeFrom : undefined}></TechnologySquare>
                                    })}
                                </tr>
                            })}
                        </tbody>
                    </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PlayerArea;